import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import i18n, { t } from 'i18next';

import ClientDataContext from '../../../../store/client-data-context';

import { Box, Stack, Typography } from '@mui/material';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { MidControlCodeData } from 'common/types/mid-control-code-data';
import * as Config from 'Config';
import { HttpSessionBreakingError, IdCardSubForm, RequestedAttributesNotification, SocketBreakingError } from '../';
import {
  EParakstsLogo,
  IdCardMonochromeLogo,
  MobileIdMonochromeLogo,
  SmartIdMonochromeLogo,
} from '../../../../assets/images';
import { Acr } from '../../../../common/types/acr';
import { Card, Select } from '../../../../components';
import isMobileDevice from '../../../../utils/mobile-check.util';
import styles from './AuthMainForm.module.css';
import { SidControlCodeData } from 'common/types/sid-control-code-data';
import SessionHttpContext from '../../../../store/session-http-context';
import { SessionLoadingSpinner } from '../../../../components/SessionLoadingSpinner/SessionLoadingSpinner';
import { AuthTabPanel } from './AuthTabPanel/AuthTabPanel';
import {
  scrollToEpsUtil,
  scrollToMidContinue,
  scrollToSidDeviceLinkBox,
  scrollToSidNotificationBox,
} from '../../../../utils/scroll-to-element.util';
import { EpsTabPanel } from './AuthTabPanel/EpsTabPanel/EpsTabPanel';
import { SidTabPanel } from './AuthTabPanel/SidTabPanel/SidTabPanel';
import { MidTabPanel } from './AuthTabPanel/MidTabPanel/MidTabPanel';

const TAC_FALLBACK_URL = Config.TERMS_AND_CONDITIONS_URL?.EN;
const PRIVACY_POLICY_FALLBACK_URL = Config.PRIVACY_POLICY_URL?.EN;

const a11yProps = (index: number) => {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
};

const INITIAL_CONTROL_CODE_DATA: MidControlCodeData = {
  authData: null,
};

const INITIAL_SID_CONTROL_CODE_DATA: SidControlCodeData = {
  authData: null,
  deviceLink: null,
};

export const AuthMainForm = () => {
  const isMobile = isMobileDevice();
  const clientCtx = useContext(ClientDataContext);
  const sessionHttpContext = useContext(SessionHttpContext);

  const deviceLinkBoxRef = useRef<HTMLDivElement>(null);
  const midContinueBtnRef = useRef<HTMLButtonElement>(null);
  const notificationBoxRef = useRef<HTMLDivElement>(null);
  const notificationContinueBtnRef = useRef<HTMLButtonElement>(null);
  const epsBoxRef = useRef<HTMLDivElement>(null);

  const currentLanguageCode = i18n.resolvedLanguage?.toUpperCase() ?? '';

  const tacUrl = Config.TERMS_AND_CONDITIONS_URL?.[currentLanguageCode] || TAC_FALLBACK_URL;
  const privacyPolicyUrl = Config.PRIVACY_POLICY_URL?.[currentLanguageCode] || PRIVACY_POLICY_FALLBACK_URL;

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [socketErrorCode, setSocketErrorCode] = useState<string>('');

  const [isLanguageChangeLoading, setIsLanguageChangeLoading] = useState(false);
  const [authCountryOptions, setAuthCountryOptions] = useState<{ label: string; value: string }[]>([]);
  const [authCountryCode, setAuthCountryCode] = useState<string>('');

  const [sidControlCodeData, setSidControlCodeData] = useState<SidControlCodeData>(INITIAL_SID_CONTROL_CODE_DATA);
  const [midControlCodeData, setMidControlCodeData] = useState<MidControlCodeData>({ ...INITIAL_CONTROL_CODE_DATA });

  const [isDeviceLinkActive, setIsDeviceLinkActive] = useState(true);
  const [displayingSidQrCode, setDisplayingSidQrCode] = useState(false);
  const [sidErrorOrigin, setSidErrorOrigin] = useState<'qr' | 'personal' | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const handleSidScroll = () => {
    const sidIndex = authMethods.indexOf('sid');

    if (!isMobile) return;
    if (selectedTabIndex !== sidIndex) return;

    if (clientCtx.client.sidFlows?.includes('device-link')) {
      scrollToSidDeviceLinkBox(deviceLinkBoxRef, isMobile);
    } else {
      setTimeout(() => {
        scrollToSidNotificationBox(notificationContinueBtnRef, isMobile);
      }, 50);
    }
  };

  const handleMidScroll = () => {
    const midIndex = authMethods.indexOf('mid');

    if (!isMobile) return;
    if (selectedTabIndex !== midIndex) return;

    scrollToMidContinue(midContinueBtnRef, isMobile);
  };

  const handleEpsScroll = () => {
    const epsIndex = authMethods.indexOf('eparaksts');

    if (!isMobile) return;
    if (selectedTabIndex !== epsIndex) return;

    scrollToEpsUtil(epsBoxRef, isMobile);
  };

  const mapAllowedCountriesToSelectOptions = useCallback(() => {
    if (clientCtx.client.allowedCountries != null) {
      setAuthCountryOptions(
        clientCtx.client.allowedCountries.map((country) => ({
          label: t(`general.country.${country.toLowerCase()}`),
          value: country,
        }))
      );
    }
  }, [clientCtx.client.allowedCountries]);

  i18n.on('languageChanged', () => {
    mapAllowedCountriesToSelectOptions();
    setIsLanguageChangeLoading(true);
    setTimeout(() => {
      setIsLanguageChangeLoading(false);
    }, 0);
  });

  const handleCountryChange = (selectedAuthCountryCode: string) => {
    setAuthCountryCode(selectedAuthCountryCode);
    setSelectedTabIndex(0);
    setSocketErrorCode('');
  };

  const authMethods = useMemo(() => {
    const result: string[] = [];
    const requestedAcrValues: string[] = clientCtx.client.acrValues ?? [];
    const isEpsEnabled = Config.ENABLE_EPARAKSTS === true;

    const addIfAllowed = (parent: Acr) => {
      if (!parent) return;
      if (parent === Acr.EPARAKSTS && !isEpsEnabled) return;
      if (!Acr.getCountries(parent).includes(authCountryCode)) return;
      if (parent === Acr.IDCARD && isMobileDevice()) return;
      if (!result.includes(parent)) result.push(parent);
    };

    if (!requestedAcrValues.length) {
      addIfAllowed(Acr.SID);
      addIfAllowed(Acr.MID);
      addIfAllowed(Acr.IDCARD);
      addIfAllowed(Acr.EPARAKSTS);
      return result;
    }

    requestedAcrValues.forEach((acrStr) => {
      if (acrStr === Acr.EIDAS_LOA_HIGH) return;
      const acrEnum = Acr.fromString(acrStr);
      const parent = Acr.getParentAcr(acrEnum);
      addIfAllowed(parent as Acr);
    });

    if (requestedAcrValues.includes(Acr.EIDAS_LOA_HIGH)) {
      addIfAllowed(Acr.MID);
      addIfAllowed(Acr.SID);
      addIfAllowed(Acr.IDCARD);
      addIfAllowed(Acr.EPARAKSTS);
    }

    return result;
  }, [authCountryCode, clientCtx.client.acrValues]);

  useEffect(() => {
    mapAllowedCountriesToSelectOptions();
  }, [mapAllowedCountriesToSelectOptions]);

  useEffect(() => {
    if (clientCtx?.client?.allowedCountries?.[0]) {
      setAuthCountryCode(clientCtx?.client?.allowedCountries?.[0]);
    }
  }, [clientCtx?.client?.allowedCountries]);

  const isSocketBreakingError = useMemo(() => {
    if (sessionHttpContext.isLoading) {
      return false;
    }

    return socketErrorCode?.toUpperCase() === 'INTERNAL_ERROR';
  }, [sessionHttpContext.isLoading, socketErrorCode]);

  const isHttpSessionBreakingError = useMemo(() => {
    if (sessionHttpContext.isLoading) {
      return false;
    }

    return !sessionHttpContext.httpResponse?.ok || Object.keys(clientCtx.client).length === 0;
  }, [sessionHttpContext.isLoading, sessionHttpContext.httpResponse?.ok, clientCtx.client]);

  if (isHttpSessionBreakingError) {
    return <HttpSessionBreakingError />;
  }

  if (isSocketBreakingError) {
    return <SocketBreakingError />;
  }

  return (
    <>
      <Card>
        {sessionHttpContext.isLoading ? (
          <SessionLoadingSpinner></SessionLoadingSpinner>
        ) : (
          <main>
            <Stack
              direction="column"
              sx={{
                gap: '1.5rem',
                mb: 4,
                visibility: isHttpSessionBreakingError ? 'hidden' : 'visible'
              }}>
              {!isMobile && (
                <Typography
                  component="h1"
                  variant="h1"
                  {...{ 'data-testid': 'loginPageTitle' }}
                  sx={{
                    textAlign: 'center'
                  }}>
                  {t(`form.main.chooseAuthenticationMethod`)}
                </Typography>
              )}

              {isLanguageChangeLoading ? (
                ''
              ) : (
                <Select
                  label={isMobile ? t('form.main.selectCountryAndMethod') : t('form.main.selectCountry')}
                  labelId="country-select"
                  items={authCountryOptions}
                  value={authCountryCode}
                  onChange={handleCountryChange}
                  {...{ 'data-testid': 'countrySelect_control' }}
                ></Select>
              )}
              <Box>
                <MuiTabs
                  slotProps={{
                    indicator: {
                      style: { display: 'none' },
                    },
                  }}
                  className={styles.tabs}
                  value={selectedTabIndex}
                  onChange={handleTabChange}
                  aria-label="Authentication tabs"
                >
                  {authMethods.map((authMethod, index) => {
                    if (authMethod === 'sid') {
                      return (
                        <MuiTab
                          key="sid"
                          className={styles.tab}
                          iconPosition="start"
                          icon={
                            <div>
                              <SmartIdMonochromeLogo />
                            </div>
                          }
                          label={t('general.sid')}
                          {...a11yProps(index)}
                          {...{ 'data-testid': 'authMethods_tab_sid-tab' }}
                          onClick={handleSidScroll}
                          onTouchEnd={handleSidScroll}
                        />
                      );
                    } else if (authMethod === 'mid') {
                      return (
                        <MuiTab
                          key="mid"
                          className={styles.tab}
                          iconPosition="start"
                          icon={
                            <div>
                              <MobileIdMonochromeLogo />
                            </div>
                          }
                          label={t('general.mid')}
                          {...a11yProps(index)}
                          {...{ 'data-testid': 'authMethods_tab_mid-tab' }}
                          onClick={handleMidScroll}
                          onTouchEnd={handleMidScroll}
                        />
                      );
                    } else if (authMethod === 'idcard') {
                      return (
                        <MuiTab
                          key="idcard"
                          className={styles.tab}
                          iconPosition="start"
                          icon={
                            <div>
                              <IdCardMonochromeLogo />
                            </div>
                          }
                          label={t('general.idcard')}
                          {...a11yProps(index)}
                          {...{ 'data-testid': 'authMethods_tab_idcard-tab' }}
                        />
                      );
                    } else if (authMethod === 'eparaksts') {
                      return (
                        <MuiTab
                          key="eparaksts"
                          className={styles.tab}
                          iconPosition="start"
                          icon={
                            <div style={{ marginTop: '4px' }}>
                              <EParakstsLogo />
                            </div>
                          }
                          label={t('general.eparaksts')}
                          {...a11yProps(index)}
                          {...{ 'data-testid': 'authMethods_tab_eparaksts-tab' }}
                          onClick={handleEpsScroll}
                          onTouchEnd={handleEpsScroll}
                        />
                      );
                    }
                  })}
                </MuiTabs>

                <Stack
                  direction="column"
                  sx={{
                    gap: '16px',
                    pt: '1rem'
                  }}>
                  {
                    !displayingSidQrCode && (
                      <RequestedAttributesNotification
                        clientData={clientCtx.client}
                        tacUrl={tacUrl}
                        privacyPolicyUrl={privacyPolicyUrl}
                      />
                    )
                  }

                  <AuthTabPanel value={selectedTabIndex} index={authMethods.indexOf('sid')}>
                    <SidTabPanel
                      sidFlows={clientCtx.client.sidFlows ?? []}
                      isMobile={isMobile}
                      authCountryCode={authCountryCode}
                      isDeviceLinkActive={isDeviceLinkActive}
                      setIsDeviceLinkActive={setIsDeviceLinkActive}
                      sidControlCodeData={sidControlCodeData}
                      setSidControlCodeData={setSidControlCodeData}
                      displayingSidQrCode={displayingSidQrCode}
                      setDisplayingSidQrCode={setDisplayingSidQrCode}
                      socketErrorCode={socketErrorCode}
                      setSocketErrorCode={setSocketErrorCode}
                      sidErrorOrigin={sidErrorOrigin}
                      setSidErrorOrigin={setSidErrorOrigin}
                      httpResponseOk={!!sessionHttpContext.httpResponse?.ok}
                      deviceLinkBoxRef={deviceLinkBoxRef}
                      notificationBoxRef={notificationBoxRef}
                      notificationContinueBtnRef={notificationContinueBtnRef}
                      initialSidControlCodeData={INITIAL_SID_CONTROL_CODE_DATA}
                    />
                  </AuthTabPanel>

                  <AuthTabPanel value={selectedTabIndex} index={authMethods.indexOf('mid')}>
                    <MidTabPanel
                      authCountryCode={authCountryCode}
                      midControlCodeData={midControlCodeData}
                      setMidControlCodeData={setMidControlCodeData}
                      socketErrorCode={socketErrorCode}
                      setSocketErrorCode={setSocketErrorCode}
                      midContinueBtnRef={midContinueBtnRef}
                      initialMidControlCodeData={INITIAL_CONTROL_CODE_DATA}
                    />
                  </AuthTabPanel>

                  <AuthTabPanel value={selectedTabIndex} index={authMethods.indexOf('idcard')}>
                    <IdCardSubForm socketErrorCode={socketErrorCode} setSocketErrorCode={setSocketErrorCode} />
                  </AuthTabPanel>

                  <AuthTabPanel value={selectedTabIndex} index={authMethods.indexOf('eparaksts')}>
                    <EpsTabPanel
                      epsBoxRef={epsBoxRef}
                      canStart={!!sessionHttpContext.httpResponse?.ok}
                      socketErrorCode={socketErrorCode}
                      setSocketErrorCode={setSocketErrorCode}
                    />
                  </AuthTabPanel>
                </Stack>
              </Box>
            </Stack>
          </main>
        )}
      </Card>
    </>
  );
};
